import { lazy, Suspense, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import {
  Activity,
  Network as NetworkIcon,
  Radio,
  Phone,
  Users,
  Shield,
  Server,
  Workflow,
  Wrench,
  LayoutDashboard,
  Gauge,
  ScanLine,
} from 'lucide-react';
import type { CapacityAlertLevel } from '@easyflow/shared';

const OverviewTab = lazy(() => import('@/components/sip-cluster/tabs/OverviewTab'));
const TopologyTab = lazy(() => import('@/components/sip-cluster/tabs/TopologyTab'));
const SignalingTab = lazy(() => import('@/components/sip-cluster/tabs/SignalingTab'));
const MediaTab = lazy(() => import('@/components/sip-cluster/tabs/MediaTab'));
const TrunksTab = lazy(() => import('@/components/sip-cluster/tabs/TrunksTab'));
const ExtensionsTab = lazy(() => import('@/components/sip-cluster/tabs/ExtensionsTab'));
const SecurityTab = lazy(() => import('@/components/sip-cluster/tabs/SecurityTab'));
const SystemTab = lazy(() => import('@/components/sip-cluster/tabs/SystemTab'));
const DialplanTab = lazy(() => import('@/components/sip-cluster/tabs/DialplanTab'));
const OpsTab = lazy(() => import('@/components/sip-cluster/tabs/OpsTab'));
const CapacityTab = lazy(() => import('@/components/sip-cluster/tabs/CapacityTab'));
const CaptureTab = lazy(() => import('@/components/sip-cluster/tabs/CaptureTab'));

type TabKey =
  | 'overview'
  | 'topology'
  | 'signaling'
  | 'media'
  | 'trunks'
  | 'extensions'
  | 'security'
  | 'system'
  | 'capacity'
  | 'dialplan'
  | 'ops'
  | 'capture';

interface TabDef {
  key: TabKey;
  label: string;
  icon: typeof Activity;
}

const TABS: readonly TabDef[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'topology', label: 'Topologia', icon: NetworkIcon },
  { key: 'signaling', label: 'Signaling', icon: Radio },
  { key: 'media', label: 'Media/Network', icon: Activity },
  { key: 'trunks', label: 'Trunks', icon: Phone },
  { key: 'extensions', label: 'Extensions', icon: Users },
  { key: 'security', label: 'Security', icon: Shield },
  { key: 'system', label: 'System', icon: Server },
  { key: 'capacity', label: 'Capacity', icon: Gauge },
  { key: 'dialplan', label: 'Dialplan', icon: Workflow },
  { key: 'ops', label: 'Ops', icon: Wrench },
  { key: 'capture', label: 'Cattura', icon: ScanLine },
] as const;

const ALERT_DOT: Record<CapacityAlertLevel, string> = {
  idle: '',
  warn: 'before:absolute before:top-1 before:right-1 before:h-2 before:w-2 before:rounded-full before:bg-amber-400',
  critical: 'before:absolute before:top-1 before:right-1 before:h-2 before:w-2 before:rounded-full before:bg-rose-500 before:animate-pulse',
};

function isTabKey(value: string | null): value is TabKey {
  return value !== null && TABS.some((t) => t.key === value);
}

function TabLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
    </div>
  );
}

export default function SipClusterMonitor() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : 'overview';
  // Task #2179 — alert level del tab Capacity (idle/warn/critical) lift dal
  // CapacityTab via callback per colorare il TabsTrigger correlato.
  const [capacityAlertLevel, setCapacityAlertLevel] = useState<CapacityAlertLevel>('idle');

  const handleTabChange = (value: string) => {
    if (!isTabKey(value)) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const tabHeader = useMemo(
    () => (
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <NetworkIcon className="h-8 w-8 text-purple-500" />
          SIP Cluster
        </h1>
        <p className="text-muted-foreground">
          NOC unificato: topologia, signaling, media, trunks, extensions, security, system, dialplan, ops.
        </p>
      </div>
    ),
    []
  );

  return (
    <div className="space-y-6">
      {tabHeader}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto p-1 gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const alertClass = t.key === 'capacity' ? ALERT_DOT[capacityAlertLevel] : '';
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className={`gap-2 relative ${alertClass}`}
                data-alert-level={t.key === 'capacity' ? capacityAlertLevel : undefined}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="overview" className="mt-0"><Suspense fallback={<TabLoader />}><OverviewTab /></Suspense></TabsContent>
        <TabsContent value="topology" className="mt-0"><Suspense fallback={<TabLoader />}><TopologyTab /></Suspense></TabsContent>
        <TabsContent value="signaling" className="mt-0"><Suspense fallback={<TabLoader />}><SignalingTab /></Suspense></TabsContent>
        <TabsContent value="media" className="mt-0"><Suspense fallback={<TabLoader />}><MediaTab /></Suspense></TabsContent>
        <TabsContent value="trunks" className="mt-0"><Suspense fallback={<TabLoader />}><TrunksTab /></Suspense></TabsContent>
        <TabsContent value="extensions" className="mt-0"><Suspense fallback={<TabLoader />}><ExtensionsTab /></Suspense></TabsContent>
        <TabsContent value="security" className="mt-0"><Suspense fallback={<TabLoader />}><SecurityTab /></Suspense></TabsContent>
        <TabsContent value="system" className="mt-0"><Suspense fallback={<TabLoader />}><SystemTab /></Suspense></TabsContent>
        <TabsContent value="capacity" className="mt-0"><Suspense fallback={<TabLoader />}><CapacityTab onAlertLevelChange={setCapacityAlertLevel} /></Suspense></TabsContent>
        <TabsContent value="dialplan" className="mt-0"><Suspense fallback={<TabLoader />}><DialplanTab /></Suspense></TabsContent>
        <TabsContent value="ops" className="mt-0"><Suspense fallback={<TabLoader />}><OpsTab /></Suspense></TabsContent>
        <TabsContent value="capture" className="mt-0"><Suspense fallback={<TabLoader />}><CaptureTab /></Suspense></TabsContent>
      </Tabs>
    </div>
  );
}
